// src/components/calendar/PersonalNotesDashboard.tsx
import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Edit, Save, Trash2, X, Plus } from "lucide-react"
import { usePersonalNotesStore } from "@/store/personalNotesStore"
import { cn } from "@/lib/utils"
import { toast } from "@/hooks/use-toast"          // ← добавлен импорт
import type { PersonalNote } from "@/store/personalNotesStore"

interface PersonalNotesDashboardProps {
  className?: string
}

export function PersonalNotesDashboard({ className }: PersonalNotesDashboardProps) {
  const { notes, addNote, updateNote, deleteNote } = usePersonalNotesStore()

  const [isAdding, setIsAdding] = useState(false)
  const [newContent, setNewContent] = useState("")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState("")

  // Автоматически открываем форму, если заметок нет
  useEffect(() => {
    if (notes.length === 0) {
      setIsAdding(true)
    }
  }, [notes.length])

  const handleAdd = async () => {
    await addNote(undefined, undefined, newContent)   // ← правильно передаём 3 параметра
    setNewContent("")
    setIsAdding(false)
  }

  const startEdit = (note: PersonalNote) => {
    setEditingId(note.id)
    setEditContent(note.content)
  }

  const handleUpdate = async (id: string) => {
    if (!editContent.trim()) return
    await updateNote(id, undefined, editContent.trim())
    setEditingId(null)
    setEditContent("")
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditContent("")
  }

  return (
    <Card
      className={cn(
        "w-full lg:w-[420px]",
        "mt-9 lg:mt-0 lg:ml-9",
        "bg-gradient-to-br from-purple-950/70 to-indigo-950/70",
        "border border-purple-600/50 rounded-3xl",
        "shadow-2xl shadow-purple-900/50 backdrop-blur-sm overflow-hidden",
        className
      )}
    >
      <CardHeader className="pb-3 border-b border-purple-700/40 bg-purple-950/40">
        <CardTitle className="text-xl font-bold text-purple-200 flex items-center gap-3">
          <Edit className="h-5 w-5 text-purple-400" />
          Мои заметки ({notes.length})
        </CardTitle>
      </CardHeader>

      <CardContent className="p-5 space-y-5">
        {/* Список всех заметок */}
        {notes.length === 0 && !isAdding ? (
          <div className="text-center py-12 text-purple-400/70 italic">
            <p className="text-lg mb-4">Нет заметок</p>
            <Button
              onClick={() => setIsAdding(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Plus className="mr-2 h-4 w-4" /> Создать первую заметку
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => (
              <div
                key={note.id}
                className="bg-purple-900/30 p-4 rounded-2xl border border-purple-600/40 group hover:bg-purple-900/50 transition-colors"
              >
                {editingId === note.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="min-h-[120px] bg-slate-900 border-purple-700 text-white placeholder:text-purple-400 resize-none focus:ring-purple-500"
                      autoFocus
                    />
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelEdit}
                        className="text-purple-300 hover:text-purple-100"
                      >
                        <X className="mr-2 h-4 w-4" /> Отмена
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleUpdate(note.id)}
                        disabled={!editContent.trim()}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      >
                        <Save className="mr-2 h-4 w-4" /> Сохранить
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <p className="text-purple-100 whitespace-pre-wrap leading-relaxed pr-12">
                      {note.content}
                    </p>
                    <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startEdit(note)}
                        className="h-8 w-8 text-purple-400 hover:text-purple-200 hover:bg-purple-900/30"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteNote(note.id)}
                        className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Кнопка добавления новой заметки */}
        {!isAdding && notes.length > 0 && (
          <Button
            onClick={() => setIsAdding(true)}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="mr-2 h-4 w-4" /> Добавить заметку
          </Button>
        )}

        {/* Форма добавления новой заметки */}
        {isAdding && (
          <div className="bg-purple-900/40 p-5 rounded-2xl border border-purple-600/40 space-y-4">
            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Ваши мысли, планы, списки... Только вы это видите"
              className="min-h-[180px] bg-slate-900 border-purple-700 text-white placeholder:text-purple-400 resize-none focus:ring-purple-500"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAdding(false)
                  setNewContent("")
                }}
                className="border-purple-600 text-purple-300 hover:bg-purple-900/50"
              >
                <X className="mr-2 h-4 w-4" /> Отмена
              </Button>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={!newContent?.trim()}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                <Save className="mr-2 h-4 w-4" /> Сохранить
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}